import React from "react";
import {
  FolderOpen,
  User,
  Users,
  Building2,
  Folder,
  CheckCircle2,
} from "lucide-react";
import HeaderMenu from "../../Navbar/headermenu";
import CustomFooter from "../../Footer/customFooter";
import { Helmet } from "react-helmet";

import "../../../assets/css/DocumentManagement.css";

const features = [
  {
    icon: <User size={26} />,
    title: "User Documents",
    description:
      "Every employee gets their own document space, mapped user-wise for easy access and tracking.",
  },
  {
    icon: <Users size={26} />,
    title: "Client Documents",
    description:
      "Files organized client-wise, so retrieval is fast and records stay clean by account.",
  },
  {
    icon: <Building2 size={26} />,
    title: "Office Documents",
    description:
      "A shared home for company-wide files and policies, alongside your user and client documents.",
  },
];

const showcase = [
  {
    eyebrow: "User Documents",
    title: "A document space that belongs to each employee",
    description:
      "Every user gets their own folder tree, kept separate and mapped to them by default — so files stay easy to find and easy to trace back to who shared them.",
    preview: {
      breadcrumb: ["Dashboard", "Documents"],
      title: "User Documents",
      scope: "krishna",
      rows: [
        { icon: <Folder size={16} />, name: "Simplifyte Pvt Ltd", kind: "Folder", sharedBy: "krishna" },
      ],
    },
  },
  {
    eyebrow: "Client Documents",
    title: "Every client's paperwork, organized client-wise",
    description:
      "Keep contracts, filings, and correspondence grouped by client instead of scattered across inboxes — with control over exactly which documents that client can see.",
    preview: {
      breadcrumb: ["Dashboard", "Documents"],
      title: "Client Documents",
      scope: "All Clients",
      rows: [
        { icon: <Folder size={16} />, name: "Acme Industries", kind: "Folder", sharedBy: "Aamir Dholia" },
        { icon: <Folder size={16} />, name: "Trupti Drug House", kind: "Folder", sharedBy: "Rutvik Bhil" },
      ],
    },
  },
  {
    eyebrow: "Office Documents",
    title: "Company-wide files, in one shared home",
    description:
      "Policies, templates, and internal documents that the whole office needs — kept separate from user and client files, but just as easy to reach.",
    preview: {
      breadcrumb: ["Dashboard", "Documents"],
      title: "Office Documents",
      scope: "Office",
      rows: [
        { icon: <Folder size={16} />, name: "HR Policies", kind: "Folder", sharedBy: "Admin" },
        { icon: <Folder size={16} />, name: "Onboarding Kit", kind: "Folder", sharedBy: "Admin" },
      ],
    },
  },
];

const benefits = [
  "Every document organized by user, client, or office — automatically",
  "Find any file in seconds with client-wise and user-wise organization",
  "Control exactly which documents each client can see",
  "Add folders and structure documents your own way, no IT required",
  "One shared home for company-wide files, alongside client work",
];

function DocPreview({ preview }) {
  return (
    <div className="DocumentManagement_class_mock_window">
      <div className="DocumentManagement_class_mock_topbar">
        <span className="DocumentManagement_class_mock_dot" />
        <span className="DocumentManagement_class_mock_dot" />
        <span className="DocumentManagement_class_mock_dot" />
      </div>

      <div className="DocumentManagement_class_mock_body">
        <div className="DocumentManagement_class_mock_breadcrumb">
          {preview.breadcrumb.map((crumb, i) => (
            <React.Fragment key={crumb}>
              <span>{crumb}</span>
              {i !== preview.breadcrumb.length - 1 && <span>&raquo;</span>}
            </React.Fragment>
          ))}
        </div>

        <h4 className="DocumentManagement_class_mock_title">
          {preview.title}
        </h4>

        <div className="DocumentManagement_class_mock_scope">
          <FolderOpen size={14} />
          {preview.scope}
        </div>

        <div className="DocumentManagement_class_mock_table">
          <div className="DocumentManagement_class_mock_header_row">
            <span>Name</span>
            <span>Kind</span>
            <span>Share By</span>
          </div>

          {preview.rows.map((row) => (
            <div key={row.name} className="DocumentManagement_class_mock_row">
              <span className="DocumentManagement_class_mock_row_name">
                {row.icon}
                {row.name}
              </span>
              <span>{row.kind}</span>
              <span>{row.sharedBy}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function DocumentsOrganized() {
  return (
    <>
      <Helmet>
        <title>Document Management | Simplifyte</title>
      </Helmet>
      <HeaderMenu />
      <div className="DocumentManagement_class_main">
        {/* Hero */}
        <section className="DocumentManagement_class_hero">
          <div className="DocumentManagement_class_container">
            <div className="DocumentManagement_class_hero_content">
              <div className="DocumentManagement_class_icon_box">
                <FolderOpen size={34} />
              </div>

              <h1 className="DocumentManagement_class_heading">
                Documents, organized exactly how your team works
              </h1>

              <p className="DocumentManagement_class_subheading">
                User documents, client documents, and office documents —
                each with its own space, so nothing gets buried in the
                wrong folder.
              </p>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="DocumentManagement_class_features_section">
          <div className="DocumentManagement_class_container">
            <h2 className="DocumentManagement_class_section_title DocumentManagement_class_center">
              Three spaces, one Documents hub
            </h2>

            <div className="DocumentManagement_class_features_grid">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="DocumentManagement_class_feature_card"
                >
                  <div className="DocumentManagement_class_feature_icon">
                    {feature.icon}
                  </div>

                  <h3 className="DocumentManagement_class_feature_title">
                    {feature.title}
                  </h3>

                  <p className="DocumentManagement_class_feature_description">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Product Showcase */}
        <section className="DocumentManagement_class_showcase_section">
          <div className="DocumentManagement_class_container">
            <h2 className="DocumentManagement_class_section_title DocumentManagement_class_center">
              See the Documents hub in action
            </h2>
            <p className="DocumentManagement_class_showcase_subtitle">
              A preview of how each document space is laid out — product
              screenshots coming soon.
            </p>

            <div className="DocumentManagement_class_showcase_list">
              {showcase.map((item, index) => (
                <div
                  key={index}
                  className={`DocumentManagement_class_showcase_row ${
                    index % 2 === 1
                      ? "DocumentManagement_class_showcase_row_reverse"
                      : ""
                  }`}
                >
                  <div className="DocumentManagement_class_showcase_text">
                    <span className="DocumentManagement_class_showcase_eyebrow">
                      {item.eyebrow}
                    </span>
                    <h3 className="DocumentManagement_class_showcase_title">
                      {item.title}
                    </h3>
                    <p className="DocumentManagement_class_showcase_description">
                      {item.description}
                    </p>
                  </div>

                  <div className="DocumentManagement_class_showcase_media">
                    <DocPreview preview={item.preview} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="DocumentManagement_class_benefits_section">
          <div className="DocumentManagement_class_container">
            <div className="DocumentManagement_class_benefits_wrapper">
              <h2 className="DocumentManagement_class_section_title DocumentManagement_class_center">
                Why teams keep documents in Simplifyte
              </h2>

              <div className="DocumentManagement_class_benefits_list">
                {benefits.map((benefit, index) => (
                  <div
                    key={index}
                    className="DocumentManagement_class_benefit_item"
                  >
                    <CheckCircle2
                      size={24}
                      className="DocumentManagement_class_check_icon"
                    />

                    <p>{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="DocumentManagement_class_cta_section">
          <div className="DocumentManagement_class_container">
            <div className="DocumentManagement_class_cta_box">
              <h2 className="DocumentManagement_class_cta_title">
                Ready to get your documents in order?
              </h2>

              <p className="DocumentManagement_class_cta_text">
                Join teams who stopped digging through email attachments
                to find the right file.
              </p>

              <button
                className="DocumentManagement_class_cta_button"
                onClick={() =>
                  (window.location.href = "https://app.simplifyte.com")
                }
              >
                Start Free Trial
              </button>
            </div>
          </div>
        </section>
      </div>
      <CustomFooter />
    </>
  );
}
